import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { getAttributionHeaders } from '@/utils/attribution';
import { API_BASE_URL } from '@/config/clientEnv';

// Track if we're currently refreshing to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];
// Track consecutive refresh failures to prevent logout loops
let refreshFailCount = 0;
const MAX_REFRESH_FAILS = 2;

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000, // 30 second timeout
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const attributionHeaders = getAttributionHeaders();
  Object.entries(attributionHeaders).forEach(([key, value]) => {
    config.headers[key] = value;
  });

  return config;
});

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => {
    // Reset fail count on successful response
    refreshFailCount = 0;
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Skip refresh logic for auth endpoints to prevent loops
    if (originalRequest?.url?.includes('/auth/refresh-token') || 
        originalRequest?.url?.includes('/auth/login') ||
        originalRequest?.url?.includes('/auth/register') ||
        originalRequest?.url?.includes('/sso/')) {
      return Promise.reject(error);
    }

    // Only handle 401 errors
    if (error.response?.status !== 401 || originalRequest?._retry) {
      return Promise.reject(error);
    }

    // Check if we've failed too many times - don't keep trying
    if (refreshFailCount >= MAX_REFRESH_FAILS) {
      console.warn('Too many refresh failures, not attempting refresh');
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Wait for the refresh to complete
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const refreshToken = useAuthStore.getState().refreshToken;
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const { data } = await axios.post(`${API_BASE_URL}/auth/refresh-token`, { refreshToken });
      const newAccessToken = data.data.accessToken;
      const newRefreshToken = data.data.refreshToken;

      useAuthStore.getState().setTokens(newAccessToken, newRefreshToken);
      refreshFailCount = 0; // Reset on success
      
      processQueue(null, newAccessToken);
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return api(originalRequest);
    } catch (refreshError: any) {
      refreshFailCount++;
      processQueue(refreshError as Error, null);
      
      // Only logout if this is a definitive auth failure (not network error)
      // and we've tried multiple times
      const isAuthError = refreshError.response?.status === 401 || refreshError.response?.status === 403;
      if (isAuthError && refreshFailCount >= MAX_REFRESH_FAILS) {
        console.warn('Refresh token invalid, logging out');
        useAuthStore.getState().logout();
      }
      
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// Auth API
export const authApi = {
  register: (data: {
    email: string;
    password: string;
    username: string;
    displayName?: string;
    userType?: string;
    referralCode?: string;
    acceptTerms?: true;
    turnstileToken?: string;
  }) => api.post('/auth/register', data),
  login: (data: { email: string; password: string; turnstileToken?: string }) => api.post('/auth/login', data),
  logout: (refreshToken: string) => api.post('/auth/logout', { refreshToken }),
  getCurrentUser: () => api.get('/auth/me'),
  changePassword: (data: { currentPassword: string; newPassword: string }) => api.post('/auth/change-password', data),
};

// Forum API
export const forumApi = {
  getCategories: () => api.get('/forum/categories'),
  getCategoryBySlug: (slug: string) => api.get(`/forum/categories/${slug}`),
  getCategoryPosts: (slug: string, params?: { page?: number; limit?: number; sort?: string }) =>
    api.get(`/forum/categories/${slug}/posts`, { params }),
  getPosts: (params?: { page?: number; limit?: number; sort?: string; country?: string; tag?: string; authorId?: string; categoryId?: string }) =>
    api.get('/forum/posts', { params }),
  getTrendingPosts: (params?: { limit?: number }) => api.get('/forum/posts/trending', { params }),
  getPostBySlug: (slug: string) => api.get(`/forum/posts/${slug}`),
  getPostComments: (slug: string) => api.get(`/forum/posts/${slug}/comments`),
  createPost: (data: { categoryId: string; title: string; content: string; tags?: string[]; countryCode?: string }) =>
    api.post('/forum/posts', data),
  updatePost: (id: string, data: { title?: string; content?: string; tags?: string[] }) =>
    api.put(`/forum/posts/${id}`, data),
  deletePost: (id: string) => api.delete(`/forum/posts/${id}`),
  getComments: (postId: string) => api.get(`/forum/posts/${postId}/comments`),
  createComment: (postId: string, data: { content: string; parentCommentId?: string }) =>
    api.post(`/forum/posts/${postId}/comments`, data),
  updateComment: (commentId: string, content: string) =>
    api.put(`/forum/comments/${commentId}`, { content }),
  deleteComment: (commentId: string) =>
    api.delete(`/forum/comments/${commentId}`),
  votePost: (id: string, voteType: 'up' | 'down') => api.post(`/forum/posts/${id}/vote`, { voteType: voteType === 'up' ? 1 : -1 }),
  voteComment: (id: string, voteType: 'up' | 'down') => api.post(`/forum/comments/${id}/vote`, { voteType: voteType === 'up' ? 1 : -1 }),
  reportPost: (id: string, reason: string) => api.post(`/forum/posts/${id}/report`, { reason }),
  getPopularTags: () => api.get('/forum/tags'),
};

// Visa Experience API
export const visaApi = {
  getExperiences: (params?: { page?: number; limit?: number; country?: string; visaType?: string; outcome?: string; authorId?: string }) =>
    api.get('/visa', { params }),
  getCountries: () => api.get('/visa/countries'),
  getCountriesWithExperiences: () => api.get('/visa/countries'),
  getCountryExperiences: (countryCode: string, params?: { visaType?: string; outcome?: string }) =>
    api.get(`/visa/countries/${countryCode}`, { params }),
  getVisaStats: () => api.get('/visa/stats'),
  getStats: () => api.get('/visa/stats'),
  getExperienceById: (id: string) => api.get(`/visa/${id}`),
  createExperience: (data: any) => api.post('/visa', data),
  updateExperience: (id: string, data: any) => api.put(`/visa/${id}`, data),
  deleteExperience: (id: string) => api.delete(`/visa/${id}`),
  upvoteExperience: (id: string) => api.post(`/visa/${id}/upvote`),
  markHelpful: (id: string) => api.post(`/visa/${id}/helpful`),
};

// Directory API
export const directoryApi = {
  getListings: (params?: { page?: number; limit?: number; category?: string; verified?: boolean; search?: string }) =>
    api.get('/business', { params }),
  getBusinesses: (params?: { page?: number; limit?: number; category?: string; country?: string; search?: string }) =>
    api.get('/business', { params }),
  getFeaturedListings: () => api.get('/business/featured'),
  getCategories: () => api.get('/business/categories'),
  getListingById: (id: string) => api.get(`/business/${id}`),
  getBusinessById: (id: string) => api.get(`/business/${id}`),
  getListingReviews: (id: string) => api.get(`/business/${id}/reviews`),
  createListing: (data: any) => api.post('/business', data),
  updateListing: (id: string, data: any) => api.put(`/business/${id}`, data),
  deleteListing: (id: string) => api.delete(`/business/${id}`),
  contactBusiness: (id: string, data: { name?: string; email?: string; message: string }) =>
    api.post(`/business/${id}/contact`, data),
};

// Deals API
export const dealsApi = {
  getDeals: (params?: { page?: number; limit?: number; category?: string; type?: string; minDiscount?: number }) =>
    api.get('/deals', { params }),
  getFeaturedDeals: () => api.get('/deals/featured'),
  getFlashDeals: () => api.get('/deals/flash'),
  getExpiringSoon: () => api.get('/deals/expiring-soon'),
  getDealById: (id: string) => api.get(`/deals/${id}`),
  createDeal: (data: any) => api.post('/deals', data),
  updateDeal: (id: string, data: any) => api.put(`/deals/${id}`, data),
  deleteDeal: (id: string) => api.delete(`/deals/${id}`),
  redeemDeal: (id: string) => api.post(`/deals/${id}/redeem`),
};

// Events API
export const eventsApi = {
  getEvents: (params?: { page?: number; limit?: number; type?: string; upcoming?: boolean }) =>
    api.get('/events', { params }),
  getUpcomingEvents: () => api.get('/events/upcoming'),
  getUpcoming: (params?: { limit?: number }) => api.get('/events/upcoming', { params }),
  getFeaturedEvents: () => api.get('/events/featured'),
  getEventById: (id: string) => api.get(`/events/${id}`),
  createEvent: (data: any) => api.post('/events', data),
  updateEvent: (id: string, data: any) => api.put(`/events/${id}`, data),
  deleteEvent: (id: string) => api.delete(`/events/${id}`),
  rsvp: (id: string, status?: string) => api.post(`/events/${id}/rsvp`, { status }),
  rsvpEvent: (id: string, status?: string) => api.post(`/events/${id}/rsvp`, { status }),
  cancelRsvp: (id: string) => api.delete(`/events/${id}/rsvp`),
  getEventAttendees: (id: string) => api.get(`/events/${id}/attendees`),
};

// Event API (alias for eventsApi)
export const eventApi = eventsApi;

// User API
export const userApi = {
  getProfile: (username: string) => api.get(`/users/${username}`),
  updateProfile: (data: any) => api.put('/users/profile', data),
  changePassword: (data: { currentPassword: string; newPassword: string }) => api.post('/auth/change-password', data),
  getUserPosts: (username: string) => api.get(`/users/${username}/posts`),
  getUserReviews: (username: string) => api.get(`/users/${username}/reviews`),
  getUserExperiences: (username: string) => api.get(`/users/${username}/experiences`),
  followUser: (userId: string) => api.post(`/users/${userId}/follow`),
  unfollowUser: (userId: string) => api.delete(`/users/${userId}/follow`),
  getNotifications: (unreadOnly?: boolean) => api.get('/users/notifications', { params: { unreadOnly } }),
  markNotificationRead: (id: string) => api.put(`/users/notifications/${id}/read`),
  markAllNotificationsRead: () => api.put('/users/notifications/read-all'),
  getBookmarks: () => api.get('/users/bookmarks'),
  addBookmark: (entityType: string, entityId: string) => api.post('/users/bookmarks', { entityType, entityId }),
  removeBookmark: (entityType: string, entityId: string) => api.delete(`/users/bookmarks/${entityType}/${entityId}`),
};

// Search API
export const searchApi = {
  globalSearch: (q: string, type?: string) => api.get('/search', { params: { q, type } }),
  searchPosts: (params: { q?: string; category?: string; country?: string; page?: number }) =>
    api.get('/search/posts', { params }),
  searchExperiences: (params: { q?: string; country?: string; visaType?: string; page?: number }) =>
    api.get('/search/experiences', { params }),
  searchBusinesses: (params: { q?: string; category?: string; page?: number }) =>
    api.get('/search/businesses', { params }),
  autocomplete: (q: string, type?: string) => api.get('/search/autocomplete', { params: { q, type } }),
  getSuggestions: () => api.get('/search/suggestions'),
};

// Reviews API
export const reviewsApi = {
  getReviews: (params?: { entityType?: string; entityId?: string; page?: number }) => api.get('/reviews', { params }),
  getByEntity: (entityType: string, entityId: string) => api.get('/reviews', { params: { entityType, entityId } }),
  createReview: (data: any) => api.post('/reviews', data),
  create: (data: any) => api.post('/reviews', data),
  markHelpful: (id: string) => api.post(`/reviews/${id}/helpful`),
};

// Messages API
export const messageApi = {
  getConversations: () => api.get('/messages/conversations'),
  getOrCreateConversation: (receiverId: string) => api.get(`/messages/conversations/${receiverId}`),
  getMessages: (conversationId: string, page?: number) =>
    api.get(`/messages/conversations/${conversationId}/messages`, { params: { page } }),
  sendMessage: (conversationId: string, content: string) =>
    api.post(`/messages/conversations/${conversationId}/messages`, { content }),
  startConversation: (receiverId: string, content: string) =>
    api.post('/messages/send', { receiverId, content }),
  getUnreadCount: () => api.get('/messages/unread-count'),
};

// Upload API
export const uploadApi = {
  uploadImage: (file: File, businessId?: string) => {
    const form = new FormData();
    form.append('image', file);
    const path = businessId ? `/uploads/business/${businessId}` : '/uploads/business';
    return api.post(path, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const catalogApi = {
  getOffers: (params?: { q?: string; type?: string; country?: string }) =>
    api.get('/v1/catalog/offers', { params }),
  getOffer: (id: string) => api.get(`/v1/catalog/offers/${id}`),
  checkout: (data: any) => api.post('/v1/catalog/checkout', data),
  nuiteeSearch: (params?: { countryCode?: string; cityName?: string }) =>
    api.get('/v1/catalog/nuitee/search', { params }),
  hotelbedsSearch: (params?: { countryCode?: string; cityName?: string }) =>
    api.get('/v1/catalog/hotelbeds/search', { params }),
  ratehawkSearch: (params?: { countryCode?: string; cityName?: string; query?: string }) =>
    api.get('/v1/catalog/ratehawk/search', { params }),
  supplierStatus: () => api.get('/v1/catalog/suppliers/status'),
  ingest: (layer: 'vendors' | 'products' | 'supplier-offers', data: any) =>
    api.post(`/v1/catalog/ingest/${layer}`, data),
  listNuiteeHotel: (hotel: any) => api.post('/v1/catalog/nuitee/list', { hotel }),
  listHotelbedsHotel: (hotel: any) => api.post('/v1/catalog/hotelbeds/list', { hotel }),
  listRatehawkHotel: (hotel: any) => api.post('/v1/catalog/ratehawk/list', { hotel }),
};

// Default export for convenience
export default api;
